import type { CSSProperties, KeyboardEvent, ReactNode, Ref } from 'react';
import { useI18n } from '../../i18n/I18nProvider.tsx';

interface SeaBattleHeaderProps {
  headerRef?: Ref<HTMLElement>;
  phone: boolean;
  authGutter: number;
  title: string;
  view: 'immersive' | 'overview';
  onImmersive(): void;
  onOverview(): void;
  onSurface(): void;
  inkTone: boolean;
  onDisplay(): void;
  language: ReactNode;
}

function closeSettings(event: KeyboardEvent<HTMLDetailsElement>): void {
  if (event.key !== 'Escape') return;
  event.stopPropagation();
  event.currentTarget.open = false;
  event.currentTarget.querySelector<HTMLElement>('summary')?.focus();
}

/** Both battle cameras stay one tap away; phones give the controls their own row. */
export function SeaBattleHeader(props: SeaBattleHeaderProps) {
  const { t } = useI18n();
  const label = (view: 'immersive' | 'overview'): string => t(`planet.hud.${view}`);
  return (
    <header ref={props.headerRef} data-testid="sea-battle-header" style={{
      ...headerStyle, flexWrap: props.phone ? 'wrap' : 'nowrap',
      paddingRight: props.phone ? 'max(10px, env(safe-area-inset-right))' : Math.max(10, props.authGutter),
    }}>
      <h1 style={{ margin: 0, minWidth: 0, flex: props.phone ? '1 0 100%' : 1,
        paddingRight: props.phone ? props.authGutter : 0, boxSizing: 'border-box',
        minHeight: props.phone ? 24 : undefined, fontSize: 13, fontWeight: 700,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <span aria-hidden style={{ color: '#8ccec3', marginRight: 7 }}>⚓</span>{props.title}
      </h1>
      <div role="group" aria-label={t('planet.hud.view')} style={{ display: 'flex', gap: 4, flex: props.phone ? 1 : undefined }}>
        {(['immersive', 'overview'] as const).map((view) => (
          <button key={view} type="button" aria-pressed={props.view === view}
            aria-label={`${t('planet.hud.interiorSea')} — ${label(view)}`}
            onClick={view === 'immersive' ? props.onImmersive : props.onOverview}
            style={{ ...buttonStyle, flex: 1, padding: '0 8px',
              background: props.view === view ? '#335c61' : buttonStyle.background,
              color: props.view === view ? '#e6fff3' : buttonStyle.color }}>
            <span aria-hidden>{view === 'immersive' ? '👣' : '🔭'} </span>{label(view)}
          </button>
        ))}
      </div>
      <button type="button" onClick={props.onSurface} style={{ ...buttonStyle, padding: '0 10px', color: '#edcf94', borderColor: '#ddc18755' }}>
        {t('planet.hud.surface')}
      </button>
      <details className="sea-battle-header-settings" style={{ position: 'relative' }} onKeyDown={closeSettings}>
        <summary aria-label={t('planet.hud.more')} title={t('planet.hud.more')}
          style={{ ...buttonStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', listStyle: 'none', width: 44, fontSize: 24, lineHeight: 1 }}>⋯</summary>
        <div role="group" aria-label={t('planet.hud.more')} style={{ position: 'absolute', right: 0, top: 47, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 184, padding: 14, borderRadius: 10, border: '1px solid #7aa99c60', background: '#102d36', color: '#d5e9df', boxShadow: '0 12px 30px #03171d66' }}>
          {props.language}
          <button type="button" data-testid="city-style-toggle" aria-pressed={props.inkTone}
            style={{ ...buttonStyle, color: props.inkTone ? '#a5dfca' : '#d5e9df' }}
            onClick={props.onDisplay}>◐ Ink + Tone</button>
        </div>
      </details>
    </header>
  );
}

const headerStyle: CSSProperties = {
  position: 'absolute', top: 0, left: 0, right: 0, zIndex: 34,
  display: 'flex', alignItems: 'center', gap: 6, padding: '2px 10px 2px max(12px, env(safe-area-inset-left))',
  background: 'linear-gradient(90deg, rgba(11,36,45,.97), rgba(10,29,39,.94))',
  color: '#d5e9df', borderBottom: '1px solid #88b6ac35', pointerEvents: 'auto',
};
const buttonStyle: CSSProperties = {
  minHeight: 44, flexShrink: 0, padding: 0, border: '1px solid #7aa99c40',
  borderRadius: 8, background: '#c0e8d905', color: '#bddbd0', cursor: 'pointer', fontSize: 12,
};
